import { useState, useEffect } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import {
  Transportista,
  fetchTransportistas,
  assignTransportista,
} from "~/services/transportistaService";

interface UseTransportistaAssignmentProps {
  salesRequestId: string;
  initialTransportistaId?: string;
  userRole: string;
  onOrderStatusChange?: () => void;
}

interface UseTransportistaAssignmentReturn {
  transportistas: Transportista[];
  assignedTransportista: Transportista | null;
  isLoading: boolean;
  isAssigning: boolean;
  handleTransportistaChange: (transportistaId: string) => Promise<void>;
}

/**
 * Hook for managing transportista assignment for sales requests
 * Only loads transportistas for superadmin and logistica users
 */
export function useTransportistaAssignment({
  salesRequestId,
  initialTransportistaId,
  userRole,
  onOrderStatusChange,
}: UseTransportistaAssignmentProps): UseTransportistaAssignmentReturn {
  const [transportistas, setTransportistas] = useState<Transportista[]>([]);
  const [assignedTransportista, setAssignedTransportista] =
    useState<Transportista | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Check if user can manage transportistas
  const canManageTransportistas =
    userRole === "superadmin" || userRole === "logistica";

  // Load transportistas and find currently assigned one
  useEffect(() => {
    const loadTransportistas = async () => {
      if (!canManageTransportistas) return;

      setIsLoading(true);
      try {
        const transportistaList = await fetchTransportistas();
        setTransportistas(transportistaList);

        // Find currently assigned transportista
        if (initialTransportistaId) {
          const assigned = transportistaList.find(
            (t) => t.id === initialTransportistaId
          );
          setAssignedTransportista(assigned || null);
        } else {
          setAssignedTransportista(null);
        }
      } catch (error) {
        console.error("Error loading transportistas:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los transportistas",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTransportistas();
  }, [initialTransportistaId, canManageTransportistas]);

  /**
   * Handle transportista assignment/unassignment
   * Pass empty string to unassign
   */
  const handleTransportistaChange = async (transportistaId: string) => {
    setIsAssigning(true);
    try {
      if (transportistaId === "") {
        // Unassign transportista
        await supabase
          .from("sales_request")
          .update({
            assigned_transportista: null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", salesRequestId);

        setAssignedTransportista(null);

        toast({
          title: "Transportista desasignado",
          description: "El transportista ha sido desasignado exitosamente",
          variant: "default",
        });
      } else {
        // Assign transportista
        await assignTransportista(salesRequestId, transportistaId);

        // Update local state
        const assigned = transportistas.find((t) => t.id === transportistaId);
        setAssignedTransportista(assigned || null);

        toast({
          title: "Transportista asignado",
          description: "El transportista ha sido asignado exitosamente",
          variant: "default",
        });
      }

      // Trigger refresh
      if (onOrderStatusChange) {
        onOrderStatusChange();
      }
    } catch (error) {
      console.error("Error with transportista assignment:", error);
      toast({
        title: "Error",
        description: "No se pudo modificar la asignación del transportista",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return {
    transportistas,
    assignedTransportista,
    isLoading,
    isAssigning,
    handleTransportistaChange,
  };
}
